import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import { yupResolver } from "@hookform/resolvers/yup";
import { mdiLoading } from "@mdi/js";
import Icon from "@mdi/react";
import { AxiosResponse } from "axios";
import * as Yup from "yup";

import Button from "components/Button";
import Modal from "components/Modal";
import Rater from "components/Rater";
import Textarea from "components/Textarea";
import { showErrorMessage, showSuccessMessage } from "helpers/toastMessage";
import { IRatingData, IReviewData } from "models/User";
import { postRatingAPI, updateRatingAPI } from "services/api/userSetting";
import { AppDispatch } from "store";
import { handleLoading } from "store/global/loader";

interface IProps {
  isRateUserModalOpen: boolean;
  toggleRateUserModal: () => void;
  isRatedUser: boolean;
  setIsRatedUser: (value: boolean) => void;
  userId: string;
  rating: IRatingData;
  setRating: React.Dispatch<React.SetStateAction<IRatingData>>;
  fetchPreviousRating: VoidFunction;
  previousRatings: IReviewData[];
  setPreviousRatings: React.Dispatch<React.SetStateAction<IReviewData[]>>;
}

interface IRateForm {
  userRate: number;
  evaluationPost: string;
}

const rateFormSchema = Yup.object<Partial<IRateForm>>({
  userRate: Yup.number().required("This field is required").moreThan(0, "This field is required"),
  evaluationPost: Yup.string()
    .required("This field is required")
    .min(15, "Please provide a longer message")
});

export default function RateUserModal({
  fetchPreviousRating,
  isRateUserModalOpen,
  toggleRateUserModal,
  isRatedUser,
  setIsRatedUser,
  userId,
  rating,
  setRating,
  previousRatings,
  setPreviousRatings
}: Readonly<IProps>) {
  const dispatch = useDispatch<AppDispatch>();
  const {
    register,
    formState: { errors, isSubmitting },
    setValue,
    reset,
    clearErrors,
    handleSubmit
  } = useForm<Partial<IRateForm>>({
    resolver: yupResolver(rateFormSchema)
  });

  const closeModal = () => {
    reset({
      userRate: 0,
      evaluationPost: ""
    });
    setIsRatedUser(false);
    setRating({ comment: "", rating: 0 });
    clearErrors();
    toggleRateUserModal();
  };

  const onSubmit = async (data: { evaluationPost: string }) => {
    if (isRatedUser) {
      showErrorMessage(
        "You have already rated this user. You may edit your previous review on the user's profile"
      );
      return;
    } else {
      try {
        dispatch(handleLoading(true));
        let updatedRatingResponse: AxiosResponse<any>;
        if (previousRatings.find((rate) => rate.to_user_id === userId)) {
          updatedRatingResponse = await updateRatingAPI(
            userId ?? "",
            rating.rating,
            data.evaluationPost
          );
        } else {
          updatedRatingResponse = await postRatingAPI(
            userId ?? "",
            rating.rating,
            data.evaluationPost
          );
        }
        const updatedRatingId = updatedRatingResponse.data.id;
        const newRatings = previousRatings.map((rating) =>
          rating.id === updatedRatingId ? updatedRatingResponse.data : rating
        );
        setPreviousRatings([...newRatings]);

        fetchPreviousRating();
        reset({
          userRate: 0,
          evaluationPost: ""
        });
        setRating({ comment: "", rating: 0 });
        showSuccessMessage("Rating submitted successfully");
        toggleRateUserModal();
      } catch (error) {
        showErrorMessage("Rating submission failed");
      } finally {
        dispatch(handleLoading(false));
      }
    }
  };

  useEffect(() => {
    if (previousRatings.find((rate) => rate.to_user_id === userId) && rating.rating) {
      reset({
        userRate: rating.rating,
        evaluationPost: rating.comment
      });
    }
  }, [previousRatings, rating]);

  return (
    <Modal
      isOpen={isRateUserModalOpen}
      closeModal={closeModal}
      wrapperClass="!my-auto desktop:!min-h-fit !px-8"
      dialogClass="!bg-foreground !opacity-95 !pt-3.5 !px-5 !pb-4 !border !border-card !overflow-y-auto gap-4"
      closeClass="!top-24 !right-8 !bg-transparent !text-placeholder">
      <form className="flex flex-col gap-4" onSubmit={handleSubmit(onSubmit)}>
        <span className="text-xl font-medium">Rate This User</span>
        <Rater
          value={rating.rating / 20}
          onChange={(newRating) => {
            setValue("userRate", newRating);
            setRating({ ...rating, rating: newRating * 20 });
          }}
        />
        {errors?.userRate ? (
          <span className="text-sm text-error">{errors.userRate?.message}</span>
        ) : null}
        <Textarea
          name="evaluationPost"
          register={register}
          rows={3}
          onChange={(e) => setRating({ ...rating, comment: e.target.value })}
          error={errors.evaluationPost}
        />
        <div className="flex justify-end flex-1 gap-3 *:rounded-full">
          <Button type="button" variant="outline" onClick={closeModal}>
            Cancel
          </Button>
          <Button type="submit" size="lg" disabled={isSubmitting}>
            {isSubmitting ? <Icon path={mdiLoading} size={2} spin={1} /> : "Submit"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
